import { actions, actionTypes, selectors } from 'data'
import { FetchAccountType, RemoveBeneficiaryActionType } from 'data/actions'
import { notifyError } from 'data/App/sagas'
import { WhitelistConfigType } from 'data/types'
import { all, call, fork, put, select, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  createAccount,
  getBeneficiaries,
  getWhitelistInfo,
  removeBeneficiary
} from 'services/api/payments'
import { Response } from 'utils/fetchApi'

import bankTransferSagas from './BankTransfer/sagas'
import paymentCurrenciesSagas from './Currencies/sagas'
import elegibleSagas from './Elegible/sagas'
import methodsSagas from './Methods/sagas'
import paymentSagas from './Payment/sagas'
import submitDepositSagas from './SubmitDeposit/sagas'
import transactionsSagas from './Transactions/sagas'
import { BeneficiaryType } from './types'
import withdrawalSagas from './Withdrawal/sagas'
import withdrawFeesAndMinSagas from './WithdrawFeesAndMin/sagas'

const fetchAccount = function* ({ payload }: FetchAccountType) {
  const { currency } = payload
  yield put(actions.payments.setAccountLoading(currency))

  const accounts = yield select(selectors.payments.getAccounts)
  const account = accounts[currency] ?? {}
  if (account.value) {
    return
  }
  const data = yield call(createAccount, currency)

  if (data.error) {
    yield put(actions.payments.setAccountError(currency, data.error))
    return
  }

  const user = yield select(selectors.auth.getUser)

  // FIXME: Hotfix for Compliance
  const accountData =
    data.response.agent &&
    data.response.agent.code &&
    data.response.agent.code === 'LHVBEE22' &&
    data.response.agent.country === null
      ? {
          ...data.response,
          agent: {
            ...data.response.agent,
            country: 'Estonia',
            recipient: `${user.firstName} ${user.lastName}`
          }
        }
      : data.response
  // TODO move to a testable function
  if (accountData.currency === 'XLM' && accountData.address) {
    const split = accountData.address.split(':')
    if (split && split.length === 2) {
      const [address, memo] = split
      accountData.address = address
      accountData.memo = memo
    }
  }
  if (accountData.currency === 'XRP' && accountData.address) {
    const split = accountData.address.split(':')
    if (split && split.length === 2) {
      const [address, tag] = split
      accountData.address = address
      accountData.tag = tag
    }
  }

  yield put(actions.payments.setAccountSuccess(currency, accountData))
}

const fetchWhitelistInfo = function* () {
  const data: Response<WhitelistConfigType> = yield call(getWhitelistInfo)

  if (data.ok) {
    yield put(actions.payments.setWhitelistAddresses(data.response.whitelisted))
    yield put(actions.payments.setWhitelistAddressesEnabled(data.response.enabled))
    yield put(actions.payments.setWhitelistCapabilities(data.response.capabilities))
  }
}

// TODO: refetch upon PGW signal
const fetchBeneficiaries = function* () {
  yield put(actions.payments.setBeneficiariesLoading())
  const data: Response<BeneficiaryType[]> = yield call(getBeneficiaries)

  if (!data.ok) {
    return yield put(actions.payments.setBeneficiariesError(data.error))
  }

  yield put(actions.payments.setBeneficiariesSuccess(data.response))
}

const removeBeneficiarySaga = function* ({ payload }: RemoveBeneficiaryActionType) {
  const data = yield call(removeBeneficiary, payload.id)

  if (!data.ok) {
    yield put(actions.payments.setBeneficiariesError(data.error))
    yield call(notifyError, 'ko_payments_remove_beneficiaries', 'KO_GENERIC')
    return
  }

  const beneficiaries = (yield select(selectors.payments.getBeneficiaries)).filter(
    ({ id }: BeneficiaryType) => id !== payload.id
  )

  yield put(actions.payments.setBeneficiariesSuccess(beneficiaries))
}

/**
 * Global and almost static data needed throughout the app
 */
const fetchData = function* ({ payload: { authenticated } }) {
  if (!authenticated) return
  const user = yield select(selectors.auth.getUser)
  if (user.state !== 'ACTIVE') return

  yield all([call(fetchBeneficiaries), call(fetchWhitelistInfo)])
}

export default function* paymentsChannel() {
  yield takeLatest(actionTypes.payments.FETCH_ACCOUNT, fetchAccount)
  yield takeLatest(actionTypes.payments.FETCH_BENEFICIARIES, fetchBeneficiaries)
  yield takeLatest(actionTypes.payments.REMOVE_BENEFICIARY, removeBeneficiarySaga)
  // @ts-ignore FIXME find out why takeEvery is not happy with string as first param
  yield takeLatest(actionTypes.auth.AUTHED, fetchData)
  yield takeEvery(actionTypes.payments.FETCH_WHITELIST_ADDRESSES, fetchWhitelistInfo)
  yield fork(bankTransferSagas)
  yield fork(paymentSagas)
  yield fork(elegibleSagas)
  yield fork(methodsSagas)
  yield fork(withdrawFeesAndMinSagas)
  yield fork(submitDepositSagas)
  yield fork(transactionsSagas)
  yield fork(paymentCurrenciesSagas)
  yield fork(withdrawalSagas)
}
